<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  services: {
    type: Array,
    default: () => [],
  },
  initialProfessionals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);
const { isAdmin } = useAdmin();

const professionals = ref(
  props.initialProfessionals && props.initialProfessionals.length
    ? [...props.initialProfessionals]
    : []
);
const isLoading = ref(false);

watch(
  () => props.initialProfessionals,
  newVal => {
    if (newVal && newVal.length && !professionals.value.length) {
      professionals.value = [...newVal];
    }
  }
);
const showModal = ref(false);
const isSaving = ref(false);
const editingProfessional = ref(null);
const modalErrorMessage = ref('');

const weekDayKeys = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const defaultWorkingHours = () => ({
  monday: { enabled: true, slots: [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }] },
  tuesday: { enabled: true, slots: [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }] },
  wednesday: { enabled: true, slots: [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }] },
  thursday: { enabled: true, slots: [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }] },
  friday: { enabled: true, slots: [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }] },
  saturday: { enabled: false, slots: [{ start: '08:00', end: '12:00' }] },
  sunday: { enabled: false, slots: [] },
});

const normalizeWorkingHours = rawHours => {
  const defaults = defaultWorkingHours();
  const result = {};

  weekDayKeys.forEach(d => {
    const dayData = rawHours?.[d.key];
    if (dayData) {
      const isEnabled = dayData.enabled !== undefined ? !!dayData.enabled : true;
      let slots = [];
      if (Array.isArray(dayData.slots) && dayData.slots.length > 0) {
        slots = dayData.slots.map(s => ({
          start: s.start || '08:00',
          end: s.end || '18:00',
          service_id: s.service_id ? Number(s.service_id) : null,
        }));
      } else if (isEnabled) {
        slots = [{ start: '08:00', end: '18:00', service_id: null }];
      }

      result[d.key] = {
        enabled: isEnabled,
        slots: slots.length > 0 ? slots : [{ start: '08:00', end: '12:00', service_id: null }],
      };
    } else {
      result[d.key] = { ...defaults[d.key] };
    }
  });

  return result;
};

const formData = ref({
  id: null,
  name: '',
  email: '',
  password: '',
  phone_number: '',
  specialty: '',
  max_concurrent_per_slot: 1,
  service_ids: [],
  working_hours: defaultWorkingHours(),
});

const assignedServices = computed(() => {
  const ids = formData.value.service_ids.map(Number);
  return props.services.filter(s => ids.includes(Number(s.id)));
});

const getValidServicesForProf = prof => {
  if (!prof.service_ids || !Array.isArray(prof.service_ids)) return [];
  const ids = prof.service_ids.map(Number);
  return props.services.filter(s => ids.includes(Number(s.id)));
};

const fetchProfessionals = async () => {
  isLoading.value = true;
  try {
    const res = await schedulingAPI.getProfessionals();
    professionals.value = res.data || [];
  } catch (err) {
    console.error('Erro ao carregar profissionais:', err);
  } finally {
    isLoading.value = false;
  }
};

const openNewProfessional = () => {
  modalErrorMessage.value = '';
  formData.value = {
    id: null,
    name: '',
    email: '',
    password: '',
    phone_number: '',
    specialty: '',
    max_concurrent_per_slot: 1,
    service_ids: props.services.map(s => s.id),
    working_hours: defaultWorkingHours(),
  };
  showModal.value = true;
};

const openEditProfessional = prof => {
  modalErrorMessage.value = '';
  const validServiceIds = props.services.map(s => Number(s.id));
  const rawIds = Array.isArray(prof.service_ids) ? prof.service_ids.map(Number) : [];
  const sanitizedIds = rawIds.filter(id => validServiceIds.includes(id));

  formData.value = {
    id: prof.id,
    name: prof.name || '',
    email: prof.email || '',
    password: '',
    phone_number: prof.phone_number || '',
    specialty: prof.specialty || '',
    max_concurrent_per_slot: prof.max_concurrent_per_slot || 1,
    service_ids: sanitizedIds,
    working_hours: normalizeWorkingHours(prof.working_hours),
  };
  showModal.value = true;
};

const toggleService = serviceId => {
  modalErrorMessage.value = '';
  const idNum = Number(serviceId);
  const index = formData.value.service_ids.indexOf(idNum);
  if (index > -1) {
    formData.value.service_ids.splice(index, 1);
    weekDayKeys.forEach(d => {
      const dayCfg = formData.value.working_hours[d.key];
      if (dayCfg && dayCfg.slots) {
        dayCfg.slots.forEach(slot => {
          if (Number(slot.service_id) === idNum) {
            slot.service_id = null;
          }
        });
      }
    });
  } else {
    formData.value.service_ids.push(idNum);
  }
};

const addTimeSlotToDay = dayKey => {
  if (!formData.value.working_hours[dayKey].slots) {
    formData.value.working_hours[dayKey].slots = [];
  }
  const existing = formData.value.working_hours[dayKey].slots;
  if (existing.length === 1 && existing[0].end <= '13:00') {
    formData.value.working_hours[dayKey].slots.push({ start: '14:00', end: '18:00', service_id: null });
  } else {
    formData.value.working_hours[dayKey].slots.push({ start: '18:00', end: '21:00', service_id: null });
  }
};

const removeTimeSlotFromDay = (dayKey, index) => {
  formData.value.working_hours[dayKey].slots.splice(index, 1);
  if (formData.value.working_hours[dayKey].slots.length === 0) {
    formData.value.working_hours[dayKey].slots.push({ start: '08:00', end: '18:00', service_id: null });
  }
};

const copyDayToAll = sourceDayKey => {
  const source = formData.value.working_hours[sourceDayKey];
  if (!source) return;

  weekDayKeys.forEach(d => {
    if (d.key !== 'saturday' && d.key !== 'sunday') {
      formData.value.working_hours[d.key] = {
        enabled: source.enabled,
        slots: source.slots.map(s => ({ ...s })),
      };
    }
  });
  alert(`Escala e serviços de ${weekDayKeys.find(w => w.key === sourceDayKey)?.label} copiados para Seg a Sex!`);
};

const saveProfessional = async () => {
  if (!formData.value.name.trim()) {
    modalErrorMessage.value = 'Por favor, informe o nome do profissional.';
    return;
  }

  isSaving.value = true;
  modalErrorMessage.value = '';

  try {
    const payload = { ...formData.value };
    if (!payload.password || !payload.password.trim()) {
      delete payload.password;
    }
    if (formData.value.id) {
      await schedulingAPI.updateProfessional(formData.value.id, payload);
    } else {
      await schedulingAPI.createProfessional(payload);
    }
    showModal.value = false;
    await fetchProfessionals();
    emit('saved');
  } catch (err) {
    const errorMsg = err.response?.data?.error || err.response?.data?.errors?.join(', ') || 'Erro ao salvar profissional.';
    modalErrorMessage.value = errorMsg;
  } finally {
    isSaving.value = false;
  }
};

const deleteProfessional = async prof => {
  if (!confirm(`Tem certeza que deseja remover o profissional "${prof.name}"?`)) return;

  try {
    await schedulingAPI.deleteProfessional(prof.id);
    await fetchProfessionals();
    emit('saved');
  } catch (err) {
    const errorMsg = err.response?.data?.error || err.response?.data?.errors?.join(', ') || 'Não é possível remover profissional com atendimentos vinculados.';
    alert(errorMsg);
  }
};

const copyPortalLink = () => {
  const url = `${window.location.origin}/portal/profissional`;
  navigator.clipboard.writeText(url);
  alert(`Link do Portal do Profissional copiado:\n${url}`);
};

onMounted(() => {
  if (professionals.value.length === 0) {
    fetchProfessionals();
  }
});
</script>

<template>
  <div class="space-y-5 select-none">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">Profissionais & Equipe</h2>
        <p class="text-xs text-n-slate-11">
          Gerencie os profissionais de atendimento, turnos múltiplos de trabalho e limites de vagas.
        </p>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 h-8 px-2.5 bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 text-xs font-medium rounded-lg border border-n-weak transition-colors cursor-pointer shadow-xs"
          title="Copiar link de acesso para o Portal do Profissional"
          @click="copyPortalLink"
        >
          <svg class="w-3.5 h-3.5 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span>Link do Portal</span>
        </button>

        <button
          v-if="isAdmin"
          type="button"
          class="inline-flex items-center gap-1.5 h-8 px-3.5 bg-brand hover:bg-brand-hover active:bg-brand text-white text-xs font-medium rounded-lg shadow-xs transition-all cursor-pointer"
          @click="openNewProfessional"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Novo Profissional</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="professionals.length === 0"
      class="p-12 text-center border border-dashed border-n-weak rounded-xl bg-n-surface-1"
    >
      <div class="w-12 h-12 rounded-full bg-brand-subtle text-n-brand flex items-center justify-center mx-auto mb-3">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
      <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Nenhum profissional cadastrado</h3>
      <p class="text-xs text-n-slate-11 max-w-sm mx-auto mb-4">
        Cadastre os profissionais da equipe para gerenciar agendas, atendimentos em grupo e escalas.
      </p>
      <button
        v-if="isAdmin"
        type="button"
        class="h-8 px-3.5 bg-brand hover:bg-brand-hover text-white text-xs font-medium rounded-lg shadow-xs cursor-pointer transition-colors"
        @click="openNewProfessional"
      >
        Cadastrar Primeiro Profissional
      </button>
    </div>

    <!-- Professionals Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="prof in professionals"
        :key="prof.id"
        class="p-4 rounded-xl bg-n-surface-1 border border-n-weak hover:border-brand transition-all shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-start justify-between gap-2 mb-3">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-brand-subtle border border-brand/20 flex items-center justify-center text-n-brand font-bold text-sm">
                {{ prof.name.charAt(0) }}
              </div>
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">{{ prof.name }}</h3>
                <span class="text-[11px] text-n-brand font-medium">{{ prof.specialty || 'Profissional Geral' }}</span>
              </div>
            </div>

            <button
              v-if="isAdmin"
              type="button"
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
              title="Editar Profissional"
              @click="openEditProfessional(prof)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>

          <div class="space-y-1.5 text-xs text-n-slate-11 mb-4">
            <p v-if="prof.phone_number" class="flex items-center gap-2">
              <span class="text-n-slate-10">📱</span>
              <span>{{ prof.phone_number }}</span>
            </p>
            <p v-if="prof.email" class="flex items-center gap-2">
              <span class="text-n-slate-10">✉️</span>
              <span class="truncate">{{ prof.email }}</span>
            </p>
            <p class="flex items-center gap-2">
              <span class="text-n-slate-10">👥</span>
              <span>Capacidade máxima: <strong class="text-n-slate-12">{{ prof.max_concurrent_per_slot || 1 }} por horário</strong></span>
            </p>
          </div>

          <!-- Services badges -->
          <div v-if="getValidServicesForProf(prof).length > 0" class="flex flex-wrap gap-1 mb-4">
            <span
              v-for="srv in getValidServicesForProf(prof)"
              :key="srv.id"
              class="text-[10px] px-2 py-0.5 rounded-md bg-n-alpha-1 dark:bg-n-solid-1 text-n-slate-11 border border-n-weak font-medium"
            >
              {{ srv.name }}
            </span>
          </div>
        </div>

        <div class="pt-3 border-t border-n-weak flex items-center justify-between">
          <button
            v-if="isAdmin"
            type="button"
            class="text-xs font-medium text-rose-600 dark:text-rose-400 hover:underline transition-colors cursor-pointer"
            @click="deleteProfessional(prof)"
          >
            Remover
          </button>
          <span v-else class="text-xs text-n-slate-10">Equipe</span>
          <span class="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">● Ativo</span>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Profissional -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showModal = false"
    >
      <div
        class="w-full max-w-xl bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 max-h-[92vh] flex flex-col"
      >
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ formData.id ? 'Editar Profissional' : 'Novo Profissional' }}
          </h3>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-sm" @click="showModal = false">✕</button>
        </div>

        <div class="p-6 space-y-4 overflow-y-auto flex-1">
          <!-- Alerta de Erro / Validação -->
          <div
            v-if="modalErrorMessage"
            class="p-3 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-lg flex items-start gap-2.5 shadow-xs animate-fade-in"
          >
            <span class="text-base shrink-0">⚠️</span>
            <span class="leading-relaxed font-medium">{{ modalErrorMessage }}</span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">Nome Completo <span class="text-rose-500">*</span></label>
              <input
                v-model="formData.name"
                type="text"
                placeholder="Ex: Dra. Juliana Costa"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">Especialidade</label>
              <input
                v-model="formData.specialty"
                type="text"
                placeholder="Ex: Fisioterapia, Pilates..."
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">Telefone / WhatsApp</label>
              <input
                v-model="formData.phone_number"
                type="text"
                placeholder="+55 11 99999-9999"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">E-mail de Login</label>
              <input
                v-model="formData.email"
                type="email"
                placeholder="profissional@empresa.com"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">
                Senha do Portal <span v-if="!formData.id" class="text-rose-500">*</span><span v-else class="text-[10px] text-n-slate-10 font-normal">(manter)</span>
              </label>
              <input
                v-model="formData.password"
                type="password"
                placeholder="Senha de acesso"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">Limite por Horário (Capacidade de Vagas Simultâneas)</label>
            <input
              v-model.number="formData.max_concurrent_per_slot"
              type="number"
              min="1"
              max="20"
              class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12 font-bold"
            />
            <span class="text-[10px] text-n-slate-10">Ex: 1 para atendimento individual, ou 4 a 5 para turmas de Pilates.</span>
          </div>

          <!-- Services Multi-Select -->
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1.5">
              Serviços que este profissional atende
            </label>
            <div class="grid grid-cols-2 gap-2 p-3 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg max-h-32 overflow-y-auto">
              <label
                v-for="s in services"
                :key="s.id"
                class="flex items-center gap-2 text-xs text-n-slate-12 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  :checked="formData.service_ids.includes(s.id)"
                  class="rounded text-n-brand cursor-pointer"
                  @change="toggleService(s.id)"
                />
                <span class="truncate">{{ s.name }}</span>
              </label>
            </div>
          </div>

          <!-- Working Hours Config per Day with MULTIPLE SLOTS / SHIFTS -->
          <div class="pt-2 border-t border-n-weak space-y-2.5">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-xs font-semibold text-n-slate-12">Grade de Horários de Atendimento</h4>
                <p class="text-[10px] text-n-slate-10">Configure múltiplos turnos (ex: manhã e tarde) para cada dia</p>
              </div>
            </div>

            <div class="space-y-2">
              <div
                v-for="day in weekDayKeys"
                :key="day.key"
                class="p-3 rounded-lg bg-n-surface-1 border border-n-weak text-xs space-y-2"
              >
                <div class="flex items-center justify-between">
                  <label class="flex items-center gap-2 font-medium cursor-pointer text-n-slate-12">
                    <input
                      v-model="formData.working_hours[day.key].enabled"
                      type="checkbox"
                      class="rounded text-n-brand focus:ring-0 cursor-pointer"
                    />
                    <span>{{ day.label }}</span>
                  </label>

                  <div v-if="formData.working_hours[day.key].enabled" class="flex items-center gap-2">
                    <button
                      type="button"
                      class="text-[10px] text-n-brand hover:underline font-medium flex items-center gap-1 cursor-pointer bg-brand-subtle px-2 py-0.5 rounded-md border border-brand/20"
                      @click="addTimeSlotToDay(day.key)"
                    >
                      <span>+ Turno / Intervalo</span>
                    </button>

                    <button
                      v-if="day.key === 'monday'"
                      type="button"
                      class="text-[10px] text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak px-2 py-0.5 rounded-md cursor-pointer transition-colors"
                      title="Copiar horários de Segunda para Terça a Sexta"
                      @click="copyDayToAll('monday')"
                    >
                      Copiar p/ Seg-Sex
                    </button>
                  </div>

                  <span v-else class="text-n-slate-9 text-[11px]">Folga / Sem Atendimento</span>
                </div>

                <!-- Multiple Shifts Rows -->
                <div v-if="formData.working_hours[day.key].enabled" class="space-y-1.5 pt-1.5 border-t border-n-weak">
                  <div
                    v-for="(slot, sIdx) in formData.working_hours[day.key].slots"
                    :key="sIdx"
                    class="flex flex-col sm:flex-row sm:items-center gap-2 bg-n-alpha-1/50 p-2 rounded-lg border border-n-weak"
                  >
                    <span class="text-[10px] text-n-slate-10 font-medium w-14 shrink-0">
                      {{ sIdx === 0 ? '1º Turno:' : `${sIdx + 1}º Turno:` }}
                    </span>

                    <!-- Horários Início e Fim -->
                    <div class="flex items-center gap-1.5 shrink-0">
                      <input
                        v-model="slot.start"
                        type="time"
                        class="h-7 px-2 text-xs bg-n-surface-1 border border-n-weak rounded-md text-n-slate-12 font-mono cursor-pointer"
                      />
                      <span class="text-n-slate-10 text-xs font-semibold">às</span>
                      <input
                        v-model="slot.end"
                        type="time"
                        class="h-7 px-2 text-xs bg-n-surface-1 border border-n-weak rounded-md text-n-slate-12 font-mono cursor-pointer"
                      />
                    </div>

                    <!-- Dropdown de Serviço do Turno -->
                    <div class="flex-1 min-w-[140px]">
                      <select
                        v-model="slot.service_id"
                        class="w-full h-7 px-2 text-[11px] bg-n-surface-1 border border-n-weak rounded-md text-n-slate-12 focus:outline-none focus:border-brand cursor-pointer"
                      >
                        <option :value="null">Todos os serviços habilitados</option>
                        <option v-for="s in assignedServices" :key="s.id" :value="s.id">
                          {{ s.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Botão Remover Turno -->
                    <button
                      v-if="formData.working_hours[day.key].slots.length > 1"
                      type="button"
                      class="text-rose-500 hover:text-rose-600 p-1 text-xs cursor-pointer ml-auto"
                      title="Remover este turno"
                      @click="removeTimeSlotFromDay(day.key, sIdx)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 py-3.5 border-t border-n-weak bg-n-alpha-1 flex items-center justify-end gap-2.5">
          <button type="button" class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors" @click="showModal = false">
            Cancelar
          </button>
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs cursor-pointer disabled:opacity-50 transition-colors"
            :disabled="isSaving"
            @click="saveProfessional"
          >
            {{ isSaving ? 'Salvando...' : 'Salvar Profissional' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
